import type { Edge, Node } from '../../../core/models/scene';
import type { AllocationLoadStatus, OrgAllocationView } from './OrgAllocationService';
import { buildOrgAllocationCanvasModel } from './OrgAllocationCanvasModel';

export interface DiagramPoint {
  readonly x: number;
  readonly y: number;
}

export interface OrgAllocationDiagramSceneOptions {
  readonly origin?: DiagramPoint;
  readonly idPrefix?: string;
  readonly maxPeople?: number;
  readonly maxTargets?: number;
}

export interface OrgAllocationDiagramScene {
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly hiddenPeopleCount: number;
  readonly hiddenTargetCount: number;
  readonly hiddenAssignmentCount: number;
}

const DEFAULT_ORIGIN: DiagramPoint = { x: 120, y: 120 };
const GRID = 12;
const PERSON_WIDTH = 300;
const PERSON_HEIGHT = 180;
const TARGET_WIDTH = 280;
const TARGET_HEIGHT = 96;
const COLUMN_GAP = 180;
const ROW_HEIGHT = 220;

/**
 * Materializes the source-backed Atlas workload view as normal diagram records.
 *
 * The source graph can be very large, so this builder intentionally emits a
 * bounded scene slice. The full graph remains in folder records; the canvas
 * receives editable, undoable diagram shapes for scenario thinking.
 */
export function buildOrgAllocationDiagramScene(
  view: OrgAllocationView,
  options: OrgAllocationDiagramSceneOptions = {},
): OrgAllocationDiagramScene {
  const model = buildOrgAllocationCanvasModel(view, {
    maxPeople: options.maxPeople,
    maxTargets: options.maxTargets,
  });
  const origin = snapPoint(options.origin ?? DEFAULT_ORIGIN);
  const idPrefix = options.idPrefix ?? `atlas-workload-${Date.now().toString(36)}`;
  const personIdByExternalId = new Map<string, string>();
  const targetIdByExternalId = new Map<string, string>();

  const peopleNodes = model.people.map((person, index) => {
    const nodeId = diagramId(idPrefix, 'person', person.id);
    personIdByExternalId.set(person.id, nodeId);
    return {
      id: nodeId,
      type: 'org_person_text',
      category: 'orgchart',
      name: person.label,
      x: origin.x,
      y: origin.y + index * ROW_HEIGHT,
      width: PERSON_WIDTH,
      height: PERSON_HEIGHT,
      w: PERSON_WIDTH,
      h: PERSON_HEIGHT,
      attrs: {
        personId: person.id,
        name: person.label,
        role: personRoleLine(person.status, person.totalAllocation, person.assignmentCount),
        customFields: [],
        accentColor: personStatusColor(person.status),
        fillColor: '#FFFFFF',
        textColor: '#04263D',
        subtitleColor: personStatusColor(person.status),
        atlasWorkload: {
          kind: 'person_load',
          externalId: person.id,
          totalAllocation: person.totalAllocation,
          assignmentCount: person.assignmentCount,
          hasUnspecifiedAllocation: person.hasUnspecifiedAllocation,
          status: person.status,
        },
      },
    } as Node;
  });

  const targetX = origin.x + PERSON_WIDTH + COLUMN_GAP;
  const targetNodes = model.targets.map((target, index) => {
    const nodeId = diagramId(idPrefix, 'target', target.id);
    const palette = targetPalette(target.targetKind);
    targetIdByExternalId.set(target.id, nodeId);
    return {
      id: nodeId,
      type: 'org_project',
      category: 'orgchart',
      name: target.label,
      x: targetX,
      y: origin.y + index * ROW_HEIGHT + Math.round((PERSON_HEIGHT - TARGET_HEIGHT) / 2),
      width: TARGET_WIDTH,
      height: TARGET_HEIGHT,
      w: TARGET_WIDTH,
      h: TARGET_HEIGHT,
      attrs: {
        name: target.label,
        projectType: target.targetKind,
        status: `${humanizeIdentifier(target.targetKind)} - ${target.peopleCount} people`,
        memberCount: target.peopleCount,
        completion: Math.round(target.totalAllocation * 100),
        customFields: [],
        accentColor: palette.accentColor,
        fillColor: palette.fillColor,
        textColor: palette.textColor,
        borderColor: palette.borderColor,
        atlasWorkload: {
          kind: 'allocation_target',
          externalId: target.id,
          targetKind: target.targetKind,
          totalAllocation: target.totalAllocation,
          peopleCount: target.peopleCount,
          hasUnspecifiedAllocation: target.hasUnspecifiedAllocation,
        },
      },
    } as Node;
  });

  const edges = model.edges
    .map((allocationEdge) => {
      const from = personIdByExternalId.get(allocationEdge.personExternalId);
      const to = targetIdByExternalId.get(allocationEdge.targetExternalId);
      if (!from || !to) return null;
      return {
        id: diagramId(idPrefix, 'allocation', allocationEdge.id),
        type: 'line',
        from,
        to,
        fromAnchor: 'right',
        toAnchor: 'left',
        fromOffset: 0.5,
        toOffset: 0.5,
        fromAnchorLocked: true,
        toAnchorLocked: true,
        attrs: {
          label: edgeLabel(
            allocationEdge.allocation,
            allocationEdge.assignmentCount,
            allocationEdge.hasUnspecifiedAllocation,
          ),
          labelOffset: 0.5,
          routingType: 'bezier',
          color: allocationEdge.hasUnspecifiedAllocation ? '#64748B' : '#2563EB',
          width: allocationEdgeWidth(allocationEdge.allocation),
          dashed: allocationEdge.hasUnspecifiedAllocation,
          dash: allocationEdge.hasUnspecifiedAllocation ? [8, 4] : undefined,
          startArrow: { type: 'none', size: 6 },
          endArrow: { type: 'chevron', size: 8 },
          atlasWorkload: {
            kind: 'capacity_commitment',
            personExternalId: allocationEdge.personExternalId,
            targetExternalId: allocationEdge.targetExternalId,
            allocation: allocationEdge.allocation,
            assignmentCount: allocationEdge.assignmentCount,
            hasUnspecifiedAllocation: allocationEdge.hasUnspecifiedAllocation,
          },
        },
      } as Edge;
    })
    .filter((edge): edge is Edge => edge !== null);

  return {
    nodes: [...peopleNodes, ...targetNodes],
    edges,
    hiddenPeopleCount: model.hiddenPeopleCount,
    hiddenTargetCount: model.hiddenTargetCount,
    hiddenAssignmentCount: model.hiddenAssignmentCount,
  };
}

export function suggestOrgAllocationDiagramOrigin(
  existingNodes: readonly Pick<Node, 'x' | 'y' | 'width' | 'height' | 'w' | 'h'>[],
): DiagramPoint {
  if (existingNodes.length === 0) return DEFAULT_ORIGIN;
  const maxRight = Math.max(
    ...existingNodes.map((node) => node.x + finiteDimension(node.width ?? node.w)),
  );
  const minTop = Math.min(...existingNodes.map((node) => node.y));
  return snapPoint({
    x: maxRight + 180,
    y: Number.isFinite(minTop) ? minTop : DEFAULT_ORIGIN.y,
  });
}

function diagramId(prefix: string, kind: string, value: string): string {
  return `${prefix}-${kind}-${stableToken(value)}`;
}

function stableToken(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36) || 'item';
  return `${slug}-${hashString(value)}`;
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 2_147_483_647;
  }
  return Math.abs(hash).toString(36);
}

function personRoleLine(
  status: AllocationLoadStatus,
  totalAllocation: number,
  assignmentCount: number,
): string {
  return `${humanizeIdentifier(status)} ${formatAllocation(totalAllocation)} - ${assignmentCount} links`;
}

function edgeLabel(
  allocation: number,
  assignmentCount: number,
  hasUnspecifiedAllocation: boolean,
): string {
  if (hasUnspecifiedAllocation && allocation <= 0) {
    return assignmentCount > 1 ? `Unknown (${assignmentCount})` : 'Unknown';
  }
  const suffix = [
    assignmentCount > 1 ? `${assignmentCount} links` : null,
    hasUnspecifiedAllocation ? 'unknown included' : null,
  ].filter(Boolean).join(' / ');
  return suffix ? `${formatAllocation(allocation)} / ${suffix}` : formatAllocation(allocation);
}

function formatAllocation(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function allocationEdgeWidth(allocation: number): number {
  return Math.max(2, Math.min(8, 2 + allocation * 5));
}

function personStatusColor(status: AllocationLoadStatus): string {
  switch (status) {
    case 'over_allocated':
      return '#DC2626';
    case 'fully_allocated':
      return '#059669';
    case 'unknown':
      return '#64748B';
    case 'under_allocated':
    default:
      return '#D97706';
  }
}

function targetPalette(kind: string): {
  readonly accentColor: string;
  readonly fillColor: string;
  readonly textColor: string;
  readonly borderColor: string;
} {
  switch (kind) {
    case 'process':
      return {
        accentColor: '#0F766E',
        fillColor: '#F0FDFA',
        textColor: '#134E4A',
        borderColor: '#5EEAD4',
      };
    case 'service':
      return {
        accentColor: '#0284C7',
        fillColor: '#F0F9FF',
        textColor: '#0C4A6E',
        borderColor: '#7DD3FC',
      };
    case 'objective':
      return {
        accentColor: '#CA8A04',
        fillColor: '#FEFCE8',
        textColor: '#713F12',
        borderColor: '#FDE68A',
      };
    case 'system':
      return {
        accentColor: '#4F46E5',
        fillColor: '#EEF2FF',
        textColor: '#312E81',
        borderColor: '#A5B4FC',
      };
    case 'project':
    default:
      return {
        accentColor: '#7C3AED',
        fillColor: '#F5F3FF',
        textColor: '#4C1D95',
        borderColor: '#C4B5FD',
      };
  }
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function snapPoint(point: DiagramPoint): DiagramPoint {
  return {
    x: Math.round(point.x / GRID) * GRID,
    y: Math.round(point.y / GRID) * GRID,
  };
}

function finiteDimension(value: number | undefined): number {
  return Number.isFinite(value) ? Number(value) : 0;
}
